import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";
import RgpdExportCard from "@/components/RgpdExportCard";
import CfoAccountantCard from "@/components/CfoAccountantCard";

const SECTIONS = {
  company: ["legal_form", "share_capital_eur", "siren", "siret", "rcs_city", "ape_code", "vat_number"],
  address: ["address", "postal_code", "city", "country"],
  contact: ["contact_email", "contact_phone"],
  payment: ["default_payment_terms_days", "default_late_penalty_text", "default_discount_text"],
};

const LONG_FIELDS = new Set(["default_late_penalty_text", "default_discount_text"]);
const NUMERIC = new Set(["share_capital_eur", "default_payment_terms_days"]);

export default function TenantSettings() {
  const { t } = useTranslation();
  const [billing, setBilling] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/tenant/settings");
      setBilling(data.billing || {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => {
    const v = e.target.value;
    setBilling({ ...billing, [k]: NUMERIC.has(k) ? (v === "" ? null : Number(v)) : v });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/tenant/settings", billing);
      toast.success(t("invoices.billing.saved"));
      await load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="p-10 text-sm text-slate-500">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="billing-settings-page">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
            style={{ fontFamily: "Manrope, sans-serif" }}>
          {t("invoices.billing.title")}
        </h1>
        <p className="mt-1 text-sm text-slate-500">{t("invoices.billing.subtitle")}</p>
      </div>

      {Object.entries(SECTIONS).map(([section, fields]) => (
        <Card key={section} className="border-slate-200 bg-white rounded-xl p-6" data-testid={`billing-section-${section}`}>
          <div className="text-[10px] font-medium uppercase tracking-widest text-slate-500 mb-4">
            {t(`invoices.billing.section.${section}`) || t(`invoices.billing.section.${section === "contact" ? "contact" : section}`)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {fields.map((f) => (
              <div key={f} className={LONG_FIELDS.has(f) ? "space-y-1.5 md:col-span-2" : "space-y-1.5"}>
                <Label htmlFor={f}>{t(`invoices.billing.fields.${f}`)}</Label>
                {LONG_FIELDS.has(f) ? (
                  <Textarea id={f} rows={2} value={billing[f] || ""} onChange={upd(f)}
                            data-testid={`billing-${f}`} />
                ) : (
                  <Input id={f}
                         type={NUMERIC.has(f) ? "number" : "text"}
                         value={billing[f] ?? ""} onChange={upd(f)}
                         data-testid={`billing-${f}`} />
                )}
              </div>
            ))}
          </div>
        </Card>
      ))}

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-billing-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white">
          <Save className="h-4 w-4 mr-1" />
          {saving ? t("common.loading") : t("invoices.billing.save")}
        </Button>
      </div>

      <RgpdExportCard />
      <CfoAccountantCard />
    </div>
  );
}
